/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { booleanAttribute, Component, EventEmitter, Input, Output } from '@angular/core';
import { createEmptyStateResource, isLoaded, StateResource } from '@common/tech-shared';
import { ButtonComponent, ButtonIconName, buttonVariants, ErrorMessageComponent, IconConfig } from '@ods/system';
import { VariantProps } from 'class-variance-authority';
import { isEmpty, isNil } from 'lodash-es';

type ButtonVariants = VariantProps<typeof buttonVariants>;

@Component({
  selector: 'odc-button-with-spinner',
  imports: [ButtonComponent, ErrorMessageComponent],
  host: { class: 'flex flex-col w-fit' },
  template: `@if (isError) {
      <ods-error-message text="Ein Fehler ist aufgetreten." subText="Versuchen Sie es noch einmal." />
    }
    <ods-button
      data-test-class="button"
      [text]="text"
      [variant]="variant"
      [size]="size"
      [type]="type"
      [dataTestId]="dataTestId"
      [dataTestClass]="dataTestClass"
      [isLoading]="isLoading"
      [disabled]="disabled"
      [destructive]="destructive"
      [icon]="icon"
      [iconConfig]="iconConfig"
      (clicked)="clicked.emit($event)"
    />`,
})
export class ButtonWithSpinnerComponent {
  @Input() set stateResource(value: StateResource<unknown>) {
    if (!isNil(value)) this._stateResource = value;
  }
  @Input() text: string = '';
  @Input() dataTestId: string = '';
  @Input() dataTestClass: string = '';
  @Input() variant: ButtonVariants['variant'] = 'primary';
  @Input() size: ButtonVariants['size'] = 'medium';
  @Input() disabled: boolean = false;
  @Input({ transform: booleanAttribute }) destructive: boolean = false;
  @Input() type: 'button' | 'submit' = 'button';
  @Input() icon?: ButtonIconName;
  @Input() iconConfig?: IconConfig;

  @Output() public clicked: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();

  _stateResource: StateResource<unknown> = createEmptyStateResource();

  get isLoading(): boolean {
    return this._stateResource.loading || this._stateResource.reload;
  }

  get isError(): boolean {
    return isLoaded(this._stateResource) && this.hasErrorMessage(this._stateResource.resource);
  }

  private hasErrorMessage(resource: unknown): boolean {
    return (
      typeof (resource as any).errorMessage !== 'undefined' &&
      !isNil((resource as any).errorMessage) &&
      !isEmpty((resource as any).errorMessage)
    );
  }
}
